import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import {
  faChevronDown,
  faStar,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";

import { UserPermissions } from "../../../hooks/use-premium-feature";
import { useRecordingJobType } from "../../../hooks/use-recording-job-type";
import { truncate } from "../../../utils";
import AutoTemplatesModal from "../../autotemplates/AutoTemplatesModal";
import PremiumFeatureGate from "../../common/premium/PremiumFeatureGate";
import {
  SCRIBENOTE_DEFAULTS,
  DEFAULT_RECORDING_TYPE,
} from "../../common/record/constants";
import DropdownSelect, {
  DropdownOption,
} from "../dropdown/DropdownSelect";

function RecordingJobTypeOption({
  option,
  classNameOverrides,
  dataTestId,
}) {
  const { name, icon, value, description } = option;
  const truncatedName = truncate(name, 15);

  return (
    <DropdownOption
      value={value}
      classNameOverrides={`!block !w-full !relative ${
        classNameOverrides || ""
      }`}
      dataTestId={dataTestId}
    >
      <div className="flex items-center justify-between w-full">
        <Tooltip title={description} arrow placement="right">
          <div className="flex items-center min-w-0">
            <FontAwesomeIcon className="mr-2 shrink-0" icon={icon} />
            <span className="truncate">{truncatedName}</span>
          </div>
        </Tooltip>
      </div>
    </DropdownOption>
  );
}

export default function RecordingJobTypeSelection({
  selectedJobType,
  setSelectedJobType,
  defaultJobType = DEFAULT_RECORDING_TYPE,
  isActive = true,
  showLabel = false,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { templates, refetchTemplates } = useRecordingJobType();

  const { enableAdaptiveTemplates } = useFlags();
  const handleTemplateSelect = (template) => {
    setSelectedJobType({
      value: template.uuid || template.value || defaultJobType.value,
      name: template.name,
      icon: template.templateType === "SOAP" ? faSoapNote : faFile,
      description:
        template.description || "Unset template description",
    });
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="relative flex flex-col items-center">
        {showLabel && (
          <label
            className={`absolute -top-4 left-0 text-xs pl-1 dark:text-gray-300 text-black`}
          >
            Templates
          </label>
        )}
        <div className="absolute right-3 top-2 pointer-events-none">
          <FontAwesomeIcon icon={faChevronDown} />
        </div>
        <DropdownSelect
          dataTestId="record-templates-dropdown-button"
          placeholder={defaultJobType.name}
          value={
            selectedJobType?.uuid ||
            selectedJobType?.value ||
            defaultJobType.value
          }
          onChange={(ev, value) => {
            if (value === "see_all") {
              setIsModalOpen(true);
              ev.preventDefault();
              return;
            }
            const selectedOption =
              templates.find((template) => template.uuid === value) ||
              SCRIBENOTE_DEFAULTS.find((opt) => opt.value === value);

            if (selectedOption) {
              setSelectedJobType({
                value: selectedOption.uuid || selectedOption.value,
                name: selectedOption.name,
                icon: selectedOption.icon,
                description: selectedOption.description,
              });
            }
          }}
          popupClassNameOverrides={`${isActive ? "block" : "hidden"}`}
          renderValue={() =>
            truncate(
              selectedJobType?.name || DEFAULT_RECORDING_TYPE.name,
              15,
            )
          }
          className="w-[200px] px-3 py-2 text-sm rounded-lg outline-none text-left dark:bg-gray-700 dark:text-gray-100 bg-gray-100 text-gray-900"
          slotProps={{
            listbox: {
              style: { fontFamily: "Inter, sans-serif" },
              className: `max-h-[400px] w-[200px] overflow-auto rounded-lg shadow-lg dark:bg-gray-800 dark:text-gray-100 border dark:border-gray-700 bg-white text-gray-900 border-gray-200 ${
                enableAdaptiveTemplates && "pb-8"
              } ${isActive ? "block" : "hidden"}`,
              autoFocus: false,
            },
            popper: {
              className: "z-50",
              style: { fontFamily: "Inter, sans-serif" },
              keepMounted: true,
              disablePortal: true,
            },
          }}
        >
          {enableAdaptiveTemplates &&
            selectedJobType?.value &&
            !SCRIBENOTE_DEFAULTS.find(
              (opt) => opt.value === selectedJobType.value,
            ) &&
            !templates.find(
              (t) =>
                t.uuid === selectedJobType.value && t.isFavourite,
            ) && (
              <>
                <div className="sticky z-10 top-0 p-1 bg-blue-50 dark:bg-blue-600 dark:text-gray-100 text-sm font-semibold">
                  Selected Unfavorited
                </div>
                <RecordingJobTypeOption
                  option={{
                    name: selectedJobType.name,
                    icon: selectedJobType.icon || faFile,
                    value: selectedJobType.value,
                    description: "Currently selected template",
                  }}
                  dataTestId={`template-option-${selectedJobType.name}`}
                />
              </>
            )}

          {/* Favorites Section */}
          {enableAdaptiveTemplates && (
            <>
              {templates.some((template) => template.isFavourite) && (
                <div
                  className="sticky z-10 top-0 p-1 bg-indigo-50 dark:bg-gray-600 dark:text-gray-100 text-sm font-semibold flex items-center"
                  data-testid="favorite-templates-header"
                >
                  <FontAwesomeIcon
                    icon={faStar}
                    className="mr-2 text-yellow-400"
                  />
                  Favorites
                </div>
              )}
              {[...templates]
                .filter((template) => template.isFavourite === true)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((template) => (
                  <PremiumFeatureGate
                    key={template.uuid}
                    featureName="Adaptive Templates"
                    iconPosition="middle-right"
                    iconSize="xs"
                    className="!block !w-full !relative"
                    requiredPermission={
                      UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
                    }
                  >
                    <RecordingJobTypeOption
                      key={template.uuid}
                      option={{
                        name: template.name,
                        icon:
                          template.templateType === "SOAP"
                            ? faSoapNote
                            : faFile,
                        value: template.uuid,
                        description: (
                          <>
                            {template.description && (
                              <div>
                                {template.description}
                                <br />
                              </div>
                            )}
                            Created by: {template?.user?.firstName}{" "}
                            {template?.user?.lastName}
                          </>
                        ),
                      }}
                      dataTestId={`template-option-${template.name}`}
                    />
                  </PremiumFeatureGate>
                ))}
            </>
          )}

          {/* Scribenote Defaults */}
          <div className="sticky top-0 p-1 border-b border-indigo-200 dark:border-indigo-500 bg-indigo-50 dark:bg-gray-800 dark:text-gray-100 text-sm font-semibold">
            Scribenote Defaults
          </div>
          {SCRIBENOTE_DEFAULTS.map((defaultOption) => (
            <RecordingJobTypeOption
              key={defaultOption.value}
              option={defaultOption}
              dataTestId={`template-option-${defaultOption.value}`}
            />
          ))}

          {enableAdaptiveTemplates && (
            <DropdownOption
              value="see_all"
              classNameOverrides="sticky bottom-0 z-20 px-2 py-1 flex w-full font-semibold bg-indigo-500 items-center justify-center cursor-pointer hover:bg-indigo-600 dark:hover:bg-indigo-600 text-white transition-colors rounded-b-xl rounded-t-none"
              dataTestId={"see-all-templates-option"}
            >
              See All Templates
            </DropdownOption>
          )}
        </DropdownSelect>
      </div>

      <AutoTemplatesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTemplateSelect={handleTemplateSelect}
        templates={templates}
        refetch={refetchTemplates}
      />
    </>
  );
}
